import type { SiteConfig, ShippingRule, Coupon, ProductSummary, StorePromotionsPayload, ShippingLocationMatch } from '@vastra/types';

export { STATIC_PAGES_DEFAULT } from './staticPagesDefault';
export { INDIA_STATES_AND_UTS } from './indiaStates';
export {
  DEFAULT_STOREFRONT_LOCALE,
  DEFAULT_ADDITIONAL_STOREFRONT_LOCALES,
  getStorefrontLocaleCodes,
  getStorefrontLocaleOptions,
  isValidAdditionalStorefrontLocaleCode,
  localeSwitcherShortLabel,
  normalizeStorefrontLocaleCode,
  parseStorefrontLocaleList,
  resolveAdditionalStorefrontLocales,
} from './storefrontLocales';

/**
 * Short prefix used across all AWS resource names, CloudFormation exports,
 * and fallback identifiers. Change this single value to rename every resource.
 * Shell and Node deploy tools read the same value via `scripts/include/resource-prefix-from-constants.cjs`
 * (see `scripts/include/resource-prefix.sh`); do not hardcode the prefix elsewhere.
 */
export const RESOURCE_PREFIX = 'stylewithvikashini';

/**
 * Fallback AWS region when `AWS_REGION` / `CDK_DEFAULT_REGION` / `AWS_DEFAULT_REGION` are unset
 * (local scripts, tests, CI defaults). Prefer setting `AWS_REGION` in deployment and CI.
 */
export const DEFAULT_AWS_REGION = 'ap-south-1';

/**
 * Secondary region used in CSP allowlists for alternate API Gateway and Cognito regional endpoints
 * (e.g. `us-east-1`). Override with env `AWS_SECONDARY_REGION` in CDK/synth only when needed.
 */
export const DEFAULT_AWS_SECONDARY_REGION = 'us-east-1';

/**
 * Public storefront origin (no trailing slash). Product images, catalog JSON paths, and other
 * `productdata/*` URLs returned by the API use this hostname — not CloudFront default domains
 * (`*.cloudfront.net`), so browsers only see branded URLs.
 */
export const CUSTOMER_SITE_ORIGIN = 'https://www.stylewithvikashini.com';

/** Public admin SPA origin (no trailing slash). Use for admin-only absolute links when needed. */
export const ADMIN_SITE_ORIGIN = 'https://admin.stylewithvikashini.com';

/** Default when published JSON has no `locationMatch` (legacy files). */
export const DEFAULT_SHIPPING_LOCATION_MATCH: ShippingLocationMatch = 'state';

/** Shown near product prices on the storefront when prices are tax-inclusive. */
export const PRICE_INCLUDES_ALL_TAXES_LABEL = 'incl. of all taxes';

export const SITE_CONFIG: SiteConfig = {
  siteName: 'Style with Vikashini',
  tagline: 'Curated Traditional Sarees',
  contactPhone: '+91 96001 96322',
  contactWhatsApp: '+919600196322',
  contactEmail: 'stylewithvikashini@yahoo.com',
  socialLinks: {
    instagram: 'https://instagram.com/stylewithvikashini',
    youtube: 'https://youtube.com/@stylewithvikashini',
    facebook: 'https://facebook.com/Stylewithvikashini',
  },
  currency: 'INR',
  currencySymbol: '₹',
};

/** Static options for admin product forms. Customer portal uses facets derived from catalog (deriveFacetsFromProducts). */
export const CATEGORIES = [
  'All',
  'Fancy Saree',
  'Jute Cotton',
  'Kalamkari Cotton',
  'Karishma Cotton',
  'Poly Cotton',
  'Mul Mul Cotton',
  'Khadi Cotton',
  'Semi Soft Silk',
  'Madurai Sungadi',
] as const;

/** Static options for admin product forms. Customer portal uses facets derived from catalog. */
export const PRODUCT_TAGS = [
  'New Arrival',
  'Best Seller',
  'Limited Edition',
  'Handcrafted',
  'Festive Special',
  'Wedding Collection',
] as const;

/** Static options for admin product forms. Customer portal uses facets derived from catalog. */
export const COLORS = [
  'Red',
  'Blue',
  'Gold',
  'White',
  'Black',
  'Pink',
  'Green',
  'Maroon',
  'Yellow',
  'Orange',
  'Purple',
  'Beige',
  'Multi',
] as const;

/** Static options for admin product forms. Customer portal uses facets derived from catalog. */
export const MATERIALS = [
  'Silk',
  'Cotton',
  'Jute Cotton',
  'Poly Cotton',
  'Mul Mul Cotton',
  'Khadi',
  'Semi Soft Silk',
  'Chiffon',
  'Georgette',
  'Linen',
  'Organza',
] as const;

/** Static options for admin product forms. Customer portal uses facets derived from catalog. */
export const OCCASIONS = [
  'Wedding',
  'Festive',
  'Casual',
  'Party',
  'Formal',
  'Daily Wear',
  'Office',
] as const;

export const ORDER_STATUSES = [
  'Pending',
  'Confirmed',
  'Processing',
  'Shipped',
  'Delivered',
  'Cancelled',
  'Returned',
  'Failed',
] as const;

/** API query `?outcome=` for order lists (reduces Dynamo reads vs loading everything). */
export const ORDER_LIST_OUTCOMES = ['successful', 'failed', 'pending', 'all'] as const;
export type OrderListOutcome = (typeof ORDER_LIST_OUTCOMES)[number];

/** Facets derived from product data (categories, colors, materials, occasions, tags). Use in customer portal; admin may use static constants for form options. */
export interface Facets {
  categories: string[];
  colors: string[];
  materials: string[];
  occasions: string[];
  tags: string[];
  /** Subcategory values present in catalog, grouped by category (for filters). */
  subcategoriesByCategory: Record<string, string[]>;
}

/** Derive filter facets from product list. Use this in the customer app so filters reflect actual catalog data (from DB/JSON). */
export function deriveFacetsFromProducts(products: ProductSummary[]): Facets {
  const stringFilter = (x: string | undefined): x is string => Boolean(x);
  const categories = Array.from(new Set(products.map((p) => p.category).filter(stringFilter))).sort((a, b) => a.localeCompare(b));
  const colors = Array.from(new Set(products.map((p) => p.color).filter(stringFilter))).sort((a, b) => a.localeCompare(b));
  const materials = Array.from(new Set(products.map((p) => p.material).filter(stringFilter))).sort((a, b) => a.localeCompare(b));
  const occasions = Array.from(new Set(products.map((p) => p.occasion).filter(stringFilter))).sort((a, b) => a.localeCompare(b));
  const allTags = products.flatMap((p) => p.tags || []).filter(stringFilter);
  const tags = Array.from(new Set(allTags)).sort((a, b) => a.localeCompare(b));
  const subcategoriesByCategory: Record<string, string[]> = {};
  for (const p of products) {
    const cat = p.category;
    const sub = p.subcategory?.trim();
    if (!cat || !sub) continue;
    if (!subcategoriesByCategory[cat]) subcategoriesByCategory[cat] = [];
    if (!subcategoriesByCategory[cat].includes(sub)) subcategoriesByCategory[cat].push(sub);
  }
  for (const k of Object.keys(subcategoriesByCategory)) {
    subcategoriesByCategory[k].sort((a, b) => a.localeCompare(b));
  }
  return {
    categories: ['All', ...categories],
    colors,
    materials,
    occasions,
    tags,
    subcategoriesByCategory,
  };
}

/** Fallback when no `shipping-rules.json` — single catch-all India zone (`states` / `pincodes` empty = any). */
export const DEFAULT_SHIPPING_RULES: ShippingRule[] = [
  {
    id: 'ship-domestic',
    region: 'India',
    baseRate: 150,
    freeShippingThreshold: 3000,
    estimatedDays: '5-7 business days',
    isActive: true,
    states: [],
    pincodes: [],
    showStatesAndUtsOnShippingPage: true,
    showPincodesOnShippingPage: true,
  },
];

export const DEFAULT_COUPONS: Coupon[] = [
  {
    code: 'SWV10',
    discountType: 'percentage',
    value: 10,
    minPurchase: 3000,
    description: '10% off on orders above ₹3,000',
    isActive: true,
  },
  {
    code: 'WELCOME500',
    discountType: 'fixed',
    value: 500,
    minPurchase: 2000,
    description: '₹500 off on your first purchase',
    isActive: true,
  },
  {
    code: 'FESTIVE20',
    discountType: 'percentage',
    value: 20,
    minPurchase: 10000,
    description: '20% off for the festive season',
    isActive: true,
    applicableCategories: ['Fancy Saree', 'Semi Soft Silk'],
    promoLabel: 'Festive 20%',
  },
];

/**
 * Fallback when promotions.json / API is unavailable (banner/hero defaults).
 * `coupons` is always empty here — published offers come only from admin / `promotions.json`.
 */
export const DEFAULT_STORE_PROMOTIONS: StorePromotionsPayload = {
  banner: {
    enabled: false,
    message: '',
    variant: 'amber',
    fontSize: 'medium',
    allowDismiss: true,
    pageTarget: 'all',
    pageTargets: ['all'],
  },
  banners: [],
  maxActiveBanners: 2,
  hero: {
    enabled: true,
    mode: 'brand',
    eyebrow: 'Curated Heritage Textiles',
    headline: 'Where Tradition Meets Timeless Elegance',
    subline:
      "Discover sarees chosen for drape, colour, and occasion — from classic weaves to contemporary styles, with the details you need on every product page.",
    primaryCtaLabel: 'Explore Collection',
    primaryCtaHref: '/products',
    secondaryCtaLabel: 'Read Our Journal',
    secondaryCtaHref: '/blog',
  },
  coupons: [],
};

/** Hard cap for how many banners can show together on the customer site. */
export const MAX_ACTIVE_PROMO_BANNERS = 2;
/** Soft cap for how many scheduled banners admins can keep in the list. */
export const MAX_SCHEDULED_PROMO_BANNERS = 12;

export const API_ENDPOINTS = {
  products: '/api/products',
  product: (id: string) => `/api/products/${id}`,
  orders: '/api/orders',
  order: (id: string) => `/api/orders/${id}`,
  blogs: '/api/blogs',
  blog: (slug: string) => `/api/blogs/${slug}`,
  auth: {
    login: '/api/auth/login',
    register: '/api/auth/register',
    me: '/api/auth/me',
    refresh: '/api/auth/refresh',
  },
  images: {
    upload: '/api/images/upload',
    presign: '/api/images/presign',
  },
  shipping: '/api/shipping',
  coupons: '/api/coupons',
  promotions: '/api/config/promotions',
  reviews: '/api/reviews',
  sitemap: '/api/sitemap',
} as const;

export const WHATSAPP_ORDER_MESSAGE = (orderId: string, items: string, total: string) =>
  encodeURIComponent(
    `Hi! I'd like to place an order from Style with Vikashini.\n\nOrder Ref: ${orderId}\nItems: ${items}\nTotal: ${total}\n\nPlease confirm availability and payment details.`
  );

export const formatPrice = (amount: number): string =>
  `₹${amount.toLocaleString('en-IN')}`;

export const generateSlug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const generateSKU = (category: string): string => {
  const prefix: Record<string, string> = {
    'Fancy Saree': 'FS',
    'Jute Cotton': 'JCS',
    'Kalamkari Cotton': 'KC',
    'Karishma Cotton': 'WCS',
    'Poly Cotton': 'PC',
    'Mul Mul Cotton': 'MMC',
    'Khadi Cotton': 'KHD',
    'Semi Soft Silk': 'SSS',
    'Madurai Sungadi': 'MSK',
  };
  const code = prefix[category] || 'PRD';
  return `${code}-${Date.now().toString(36).toUpperCase().slice(-4)}`;
};
